// =====================================================================
// Faz AW (2026-05-07) — Stok Manuel Düzeltme
// =====================================================================
// Owner depoda fiziksel sayım yapıp fark olunca manuel düzeltir.
// Audit log'lu, geri alınamaz (hesap muhasebesi).
//
// Sayfa 2 panel:
//   - "Yeni Düzeltme" formu (ürün ara + delta + reason + notes)
//   - Son hareketler tablosu (son 30 gün, filter'lı)
//
// Endpoint:
//   POST /api/admin/products/:id/adjust-stock
//   GET  /api/admin/stock-adjustments?days=30&reason=...
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button,
  Autocomplete, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Avatar,
  ButtonGroup
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

// 8 reason — Türkçe etiket + ikon + renk
const REASON_META = {
  physical_count:  { label: "Fiziksel Sayım", icon: "📋", color: "#3b82f6" },
  damaged:         { label: "Hasarlı / Zayi",  icon: "💥", color: "#ef4444" },
  theft:           { label: "Çalıntı / Kayıp", icon: "🚨", color: "#dc2626" },
  return:          { label: "İade Re-stock",   icon: "↩️", color: "#10b981" },
  correction:      { label: "Hata Düzeltme",   icon: "🔧", color: "#f59e0b" },
  transfer_in:     { label: "Şubeden Geliş",   icon: "📥", color: "#10b981" },
  transfer_out:    { label: "Şubeye Çıkış",    icon: "📤", color: "#f97316" },
  other:           { label: "Diğer",           icon: "❓", color: "#94a3b8" }
};

export default function StockAdjustments() {
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Filter
  const [reasonFilter, setReasonFilter] = useState("all");
  const [days, setDays] = useState(30);

  // Yeni düzeltme formu
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deltaSign, setDeltaSign] = useState("plus");  // plus | minus
  const [deltaAmount, setDeltaAmount] = useState("");
  const [reason, setReason] = useState("physical_count");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const reasonParam = reasonFilter === "all" ? "" : `&reason=${reasonFilter}`;
      const [historyR, productsR] = await Promise.allSettled([
        API.get(`/api/admin/stock-adjustments?days=${days}${reasonParam}`),
        // Yeni düzeltme formu için ürün listesi
        history.length === 0 ? API.get("/api/admin/products?limit=500") : Promise.resolve({ data: products })
      ]);
      // Array coercion — server response shape değişikliklerine karşı koruma
      // (Subscriptions.js'de yaşanan TypeError 'c is not a function' bug'ının önlenmesi)
      const toArr = (raw) => Array.isArray(raw) ? raw
                          : Array.isArray(raw?.data) ? raw.data
                          : Array.isArray(raw?.items) ? raw.items : [];
      setHistory(historyR.status === "fulfilled" ? toArr(historyR.value.data?.items) : []);
      setSummary(historyR.status === "fulfilled" ? toArr(historyR.value.data?.summary) : []);
      if (productsR.status === "fulfilled" && history.length === 0) {
        setProducts(toArr(productsR.value.data));
      }
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };
  // async load → useEffect cleanup Promise patlamasını önle (arrow wrap)
  useEffect(() => { load(); }, [reasonFilter, days]);

  const submitAdjustment = async () => {
    if (!selectedProduct) { toast.error("Ürün seçin"); return; }
    const amount = parseInt(deltaAmount, 10);
    if (isNaN(amount) || amount <= 0) { toast.error("Geçerli sayı girin"); return; }
    if (reason === "other" && (!notes || notes.trim().length < 5)) {
      toast.error('"Diğer" sebep için en az 5 karakter açıklama gerek'); return;
    }

    const delta = deltaSign === "plus" ? amount : -amount;
    setSaving(true);
    try {
      const r = await API.post(`/api/admin/products/${selectedProduct.id}/adjust-stock`, {
        delta, reason, notes
      });
      toast.success(
        `✓ ${r.data.product_name}: ${r.data.stock_before} → ${r.data.stock_after} (${delta > 0 ? '+' : ''}${delta})`
      );
      // Form sıfırla
      setSelectedProduct(null);
      setDeltaAmount("");
      setNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && history.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  // Özet net delta hesabı
  const totalNetDelta = summary.reduce((s, r) => s + Number(r.total_delta || 0), 0);
  const totalAdjustments = summary.reduce((s, r) => s + Number(r.count || 0), 0);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Stok Manuel Düzeltme</Typography>
          <Typography variant="body2" color="text.secondary">
            Fiziksel sayım, hasar, transfer — audit log'lu stok hareketi
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={7}>7g</ToggleButton>
            <ToggleButton value={30}>30g</ToggleButton>
            <ToggleButton value={90}>90g</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Yeni düzeltme formu */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3, bgcolor: "#f8fafc", border: "2px solid #1565C0" }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          ➕ Yeni Düzeltme
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={products}
              value={selectedProduct}
              onChange={(_, v) => setSelectedProduct(v)}
              getOptionLabel={(p) => `${p.name}${p.sku ? ` [${p.sku}]` : ""}`}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              renderInput={(params) => (
                <TextField {...params} label="Ürün Ara *" size="small"
                  helperText={selectedProduct ? `Mevcut stok: ${selectedProduct.stock || 0}` : "Ürün adı veya SKU ile ara"}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Box sx={{ display: "flex", gap: 0 }}>
              <ButtonGroup size="small" sx={{ flex: 0 }}>
                <Button
                  onClick={() => setDeltaSign("plus")}
                  variant={deltaSign === "plus" ? "contained" : "outlined"}
                  color="success" sx={{ minWidth: 40 }}
                >
                  <AddIcon fontSize="small" />
                </Button>
                <Button
                  onClick={() => setDeltaSign("minus")}
                  variant={deltaSign === "minus" ? "contained" : "outlined"}
                  color="error" sx={{ minWidth: 40 }}
                >
                  <RemoveIcon fontSize="small" />
                </Button>
              </ButtonGroup>
              <TextField
                fullWidth type="number" label="Adet *" size="small"
                value={deltaAmount}
                onChange={e => setDeltaAmount(e.target.value)}
                helperText={
                  selectedProduct && deltaAmount
                    ? `Yeni stok: ${(Number(selectedProduct.stock) || 0) + (deltaSign === "plus" ? 1 : -1) * (parseInt(deltaAmount, 10) || 0)}`
                    : "Pozitif değer gir, +/- buton seç"
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={8} md={4}>
            <TextField
              select fullWidth label="Sebep *" size="small"
              value={reason}
              onChange={e => setReason(e.target.value)}
            >
              {Object.entries(REASON_META).map(([k, m]) => (
                <MenuItem key={k} value={k}>{m.icon} {m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth size="small" multiline rows={2}
              label={reason === "other" ? "Açıklama (zorunlu)" : "Açıklama (opsiyonel)"}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Örn: 'Salı sayımı, gerçekte 480 vardı' / 'Yangın hasarı 12 koli atıldı'"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button onClick={() => { setSelectedProduct(null); setDeltaAmount(""); setNotes(""); }}>
                Temizle
              </Button>
              <Button
                variant="contained"
                onClick={submitAdjustment}
                disabled={saving || !selectedProduct || !deltaAmount}
                color={deltaSign === "plus" ? "success" : "error"}
              >
                {saving ? "Kaydediliyor..." : (deltaSign === "plus" ? "Stok Ekle" : "Stok Çıkar")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Reason özet KPI'ları + filter */}
      {summary.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            REASON ÖZETİ ({days} gün) — tıkla filtrele
          </Typography>
          <Grid container spacing={1.5} mb={3} mt={0}>
            <Grid item xs={6} md={2}>
              <Card
                onClick={() => setReasonFilter("all")}
                sx={{
                  p: 1.5, borderRadius: 3, cursor: "pointer",
                  border: reasonFilter === "all" ? "2px solid #1565C0" : "2px solid transparent"
                }}
              >
                <Typography variant="caption" fontWeight={700}>TÜMÜ</Typography>
                <Typography variant="h6" fontWeight={800}>{totalAdjustments}</Typography>
                <Typography variant="caption" sx={{ color: totalNetDelta >= 0 ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                  Net: {totalNetDelta >= 0 ? "+" : ""}{totalNetDelta}
                </Typography>
              </Card>
            </Grid>
            {summary.map(s => {
              const m = REASON_META[s.reason] || REASON_META.other;
              return (
                <Grid item xs={6} md={2} key={s.reason}>
                  <Card
                    onClick={() => setReasonFilter(s.reason)}
                    sx={{
                      p: 1.5, borderRadius: 3, cursor: "pointer",
                      border: reasonFilter === s.reason ? `2px solid ${m.color}` : "2px solid transparent",
                      bgcolor: reasonFilter === s.reason ? `${m.color}11` : "white"
                    }}
                  >
                    <Typography variant="caption" sx={{ color: m.color, fontWeight: 700 }}>
                      {m.icon} {m.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>{s.count}</Typography>
                    <Typography variant="caption" sx={{ color: Number(s.total_delta) >= 0 ? "#10b981" : "#ef4444", fontWeight: 700 }}>
                      {Number(s.total_delta) >= 0 ? "+" : ""}{s.total_delta}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* History tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={700}>Stok Hareket Geçmişi</Typography>
          <Typography variant="caption" color="text.secondary">
            {reasonFilter === "all" ? `Son ${days} günün tüm değişimleri` : `Sebep: ${REASON_META[reasonFilter]?.label}`}
          </Typography>
        </Box>
        {history.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Bu kriterlerle hareket bulunamadı</Alert>
          </Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Tarih</strong></TableCell>
                <TableCell><strong>Ürün</strong></TableCell>
                <TableCell align="center"><strong>Önce</strong></TableCell>
                <TableCell align="center"><strong>Delta</strong></TableCell>
                <TableCell align="center"><strong>Sonra</strong></TableCell>
                <TableCell><strong>Sebep</strong></TableCell>
                <TableCell><strong>Admin / Not</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map(h => {
                const m = REASON_META[h.reason] || REASON_META.other;
                const delta = Number(h.delta);
                return (
                  <TableRow key={h.id} hover>
                    <TableCell sx={{ fontSize: 11 }}>{fmtDate(h.created_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar src={h.image_url} variant="rounded" sx={{ width: 32, height: 32, bgcolor: "#f1f5f9" }}>📦</Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={600}>{h.product_name}</Typography>
                          <Typography variant="caption" color="text.disabled" fontSize={10}>{h.sku}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{h.stock_before}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={delta > 0 ? `+${delta}` : delta}
                        size="small"
                        sx={{
                          bgcolor: delta > 0 ? "#10b981" : "#ef4444",
                          color: "white", fontWeight: 800, minWidth: 50
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>{h.stock_after}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${m.icon} ${m.label}`}
                        size="small"
                        sx={{ bgcolor: m.color + "22", color: m.color, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{h.admin_name || "—"}</Typography>
                      {h.notes && (
                        <Typography variant="caption" color="text.disabled" fontSize={11} sx={{ fontStyle: "italic" }}>
                          {h.notes}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
}
